/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import type { MtaKitPricingZoneStoryblok } from '@/lib/storyblok/types/generated-types'
import { countries } from '@/utils/countries'

/** Type alias for the pricing zone names. */
type PricingZone = MtaKitPricingZoneStoryblok['zone']

/** Create a lookup map for country names to codes for efficiency. */
const countryNameToCodeMap = new Map<string, string>()
countries.forEach((country) => {
  countryNameToCodeMap.set(country.name.toLowerCase(), country.code)
})

/** Zone mappings based on the configuration on WooCommerce. */
const zoneCountryCodes: Record<PricingZone, string[]> = {
  uk: ['GB'], // United Kingdom
  north_america: [
    'AG', // Antigua and Barbuda
    'BS', // Bahamas
    'BB', // Barbados
    'CA', // Canada
    'DM', // Dominica
    'GD', // Grenada
    'GT', // Guatemala
    'JM', // Jamaica
    'KN', // Saint Kitts and Nevis
    'LC', // Saint Lucia
    'VC', // Saint Vincent and the Grenadines
    'TT', // Trinidad and Tobago
    'US', // United States
    'UM', // United States Minor Outlying Islands
  ].filter((code): code is string => code !== null),
  asia: [
    'BD', // Bangladesh
    'BT', // Bhutan
    'BN', // Brunei
    'KH', // Cambodia
    'CN', // China
    'IN', // India
    'ID', // Indonesia
    'JP', // Japan
    'LA', // Laos
    'MY', // Malaysia
    'MV', // Maldives
    'MM', // Myanmar
    'NP', // Nepal
    'KP', // North Korea
    'PH', // Philippines
    'RU', // Russia
    'SG', // Singapore
    'KR', // South Korea
    'TW', // Taiwan
    'TH', // Thailand
    'VN', // Vietnam
  ].filter((code): code is string => code !== null),
  australasia: [
    'AU', // Australia
    'NZ', // New Zealand
  ].filter((code): code is string => code !== null),
  eu: [
    'AX', // Åland Islands
    'AD', // Andorra
    'AT', // Austria
    'BE', // Belgium
    'CY', // Cyprus
    'EE', // Estonia
    'FI', // Finland
    'FR', // France
    'GF', // French Guiana
    'TF', // French Southern Territories
    'DE', // Germany
    'GR', // Greece
    'GP', // Guadeloupe
    'IE', // Ireland
    'IT', // Italy
    'LV', // Latvia
    'LT', // Lithuania
    'LU', // Luxembourg
    'MT', // Malta
    'MQ', // Martinique
    'YT', // Mayotte
    'MC', // Monaco
    'ME', // Montenegro
    'NL', // Netherlands
    'PT', // Portugal
    'RE', // Reunion
    'BL', // Saint Barthélemy
    'MF', // Saint Martin (French part)
    'PM', // Saint Pierre and Miquelon
    'SM', // San Marino
    'SK', // Slovakia
    'SI', // Slovenia
    'ES', // Spain
    'VA', // Vatican
  ].filter((code): code is string => code !== null),
  latam: [
    'AR', // Argentina
    'BZ', // Belize
    'BO', // Bolivia
    'BR', // Brazil
    'CL', // Chile
    'CO', // Colombia
    'CR', // Costa Rica
    'CU', // Cuba
    'DO', // Dominican Republic
    'EC', // Ecuador
    'SV', // El Salvador
    'HT', // Haiti
    'HN', // Honduras
    'MX', // Mexico
    'NI', // Nicaragua
    'PA', // Panama
    'PY', // Paraguay
    'PE', // Peru
    'PR', // Puerto Rico
    'UY', // Uruguay
    'VE', // Venezuela
  ].filter((code): code is string => code !== null),
  mena: [
    'AF', // Afghanistan
    'DZ', // Algeria
    'AZ', // Azerbaijan
    'BH', // Bahrain
    'EG', // Egypt
    'GE', // Georgia
    'IR', // Iran
    'IQ', // Iraq
    'IL', // Israel
    'JO', // Jordan
    'KW', // Kuwait
    'LB', // Lebanon
    'LY', // Libya
    'MR', // Mauritania
    'MA', // Morocco
    'OM', // Oman
    'PK', // Pakistan
    'PS', // Palestinian Territory
    'QA', // Qatar
    'SA', // Saudi Arabia
    'SO', // Somalia
    'SD', // Sudan
    'SY', // Syria
    'TN', // Tunisia
    'TR', // Turkey
    'AE', // United Arab Emirates
    'EH', // Western Sahara
  ].filter((code): code is string => code !== null),
  subsahara: [
    'AO', // Angola
    'BJ', // Benin
    'BW', // Botswana
    'BF', // Burkina Faso
    'BI', // Burundi
    'CM', // Cameroon
    'CV', // Cape Verde
    'CF', // Central African Republic
    'TD', // Chad
    'KM', // Comoros
    'CG', // Congo (Brazzaville)
    'CD', // Congo (Kinshasa)
    'GQ', // Equatorial Guinea
    'ER', // Eritrea
    'ET', // Ethiopia
    'GA', // Gabon
    'GM', // Gambia
    'GH', // Ghana
    'GN', // Guinea
    'GW', // Guinea-Bissau
    'CI', // Ivory Coast
    'KE', // Kenya
    'LS', // Lesotho
    'LR', // Liberia
    'MG', // Madagascar
    'MW', // Malawi
    'ML', // Mali
    'MU', // Mauritius
    'MZ', // Mozambique
    'NA', // Namibia
    'NE', // Niger
    'NG', // Nigeria
    'RW', // Rwanda
    'SN', // Senegal
    'SC', // Seychelles
    'SL', // Sierra Leone
    'ZA', // South Africa
    'SS', // South Sudan
    'TZ', // Tanzania
    'TG', // Togo
    'UG', // Uganda
    'ZM', // Zambia
    'ZW', // Zimbabwe
  ].filter((code): code is string => code !== null),
}

// Build a reverse map from country code to zone for faster lookup
const countryCodeToZoneMap = new Map<string, PricingZone>()
for (const zone in zoneCountryCodes) {
  if (Object.prototype.hasOwnProperty.call(zoneCountryCodes, zone)) {
    const codes = zoneCountryCodes[zone as PricingZone]
    codes.forEach((code) => {
      countryCodeToZoneMap.set(code, zone as PricingZone)
    })
  }
}

/**
 * Maps an ISO 3166-1 alpha-2 country code to a pricing zone based on the defined zone mappings.
 *
 * @param countryCode - The ISO 3166-1 alpha-2 country code (e.g., 'GB', 'US').
 */
const getPricingZoneByCountryCode = (countryCode: string): PricingZone | null => {
  return countryCodeToZoneMap.get(countryCode.toUpperCase()) ?? null
}

export { getPricingZoneByCountryCode }
export type { PricingZone }
